import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { billingKeys } from '@/entities/billing/model/keys'
import { PERMISSIONS_QUERY_KEY } from '@/entities/session/model/keys'
import { BillingFeature } from '@/features/billing/ui/BillingFeature'
import { PageHeader } from '@/shared/components/layout/PageHeader'
import { PermissionGuard } from '@/shared/components/PermissionGuard'
import { PERMISSIONS } from '@/shared/config/permissions'

// `limit` and `plan` are written by the upgrade link built from a 402 refusal. `billing=returned` is
// written by the return URL we hand Stripe when opening its hosted billing portal. Anything
// unrecognised is ignored rather than rejected, so a hand-edited URL still opens the page.
const billingSearchSchema = z.object({
  limit: z.enum(['sites', 'users']).optional().catch(undefined),
  plan: z.string().optional().catch(undefined),
  billing: z.enum(['returned']).optional().catch(undefined),
})

export const Route = createFileRoute('/settings/billing/')({
  validateSearch: billingSearchSchema,
  component: BillingRoute,
  staticData: { authorize: true },
})

function BillingRoute() {
  const { limit, plan, billing } = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Captured once, on mount, because the effect below strips the parameter from the URL immediately.
  // Without holding it in state the note would appear and vanish in the same frame.
  const [returnedFromPortal] = useState(billing === 'returned')

  useEffect(() => {
    if (billing !== 'returned') return

    // Our view of Stripe may be stale: the customer could have replaced a card or paid an invoice,
    // and the only thing that tells us is a webhook. The subscription query is already
    // `staleTime: 0` with `refetchOnMount`, so a full-page return refetches it anyway — this is
    // explicit so the refresh does not depend on that staying true, and so the PERMISSIONS are
    // refetched too: a payment moves the account out of PastDue, and what the office may do is
    // projected from that.
    void queryClient.invalidateQueries({ queryKey: billingKeys.all })
    void queryClient.invalidateQueries({ queryKey: PERMISSIONS_QUERY_KEY })

    // Then take the marker out of the URL, so a reload or a bookmark does not replay the return for
    // ever. `replace` so the back button does not walk into it either. The other parameters are
    // preserved explicitly rather than spread, because they are the whole of this route's schema.
    void navigate({ to: '/settings/billing', search: { limit, plan }, replace: true })
  }, [billing, limit, plan, navigate, queryClient])

  return (
    <div className="space-y-6">
      <PageHeader
        title="Subscription"
        description="What your organisation pays Nezuna for NezTrack, and how much of the plan is in use."
        breadcrumbs={[
          { label: 'Settings', href: '/settings' },
          { label: 'Subscription' },
        ]}
      />
      <PermissionGuard
        permission={PERMISSIONS.BillingView}
        fallback={
          <p className="text-sm text-muted-foreground">
            You don't have permission to view the subscription.
          </p>
        }
      >
        <BillingFeature limitHit={limit} returnedFromPortal={returnedFromPortal} />
      </PermissionGuard>
    </div>
  )
}
